// PersistenceManager: IndexedDB storage for tabs and app state
import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { PersistedTab } from './types';

interface EditorDB extends DBSchema {
  tabs: {
    key: string;
    value: PersistedTab;
  };
  appState: {
    key: string;
    value: any;
  };
}

export class PersistenceManager {
  private db: IDBPDatabase<EditorDB> | null = null;
  private readonly DB_NAME = 'TextEditorPWA';
  private readonly DB_VERSION = 1;

  async init(): Promise<void> {
    this.db = await openDB<EditorDB>(this.DB_NAME, this.DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('tabs')) {
          db.createObjectStore('tabs', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('appState')) {
          db.createObjectStore('appState', { keyPath: 'key' });
        }
      },
    });
  }

  async saveTabs(tabs: PersistedTab[]): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    const tx = this.db.transaction('tabs', 'readwrite');
    await Promise.all(tabs.map((tab) => tx.store.put(tab)));
    await tx.done;
  }

  async loadTabs(): Promise<PersistedTab[]> {
    if (!this.db) throw new Error('Database not initialized');
    return await this.db.getAll('tabs');
  }

  async saveTab(tab: PersistedTab): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    await this.db.put('tabs', tab);
  }

  async clearTab(tabId: string): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    await this.db.delete('tabs', tabId);
  }

  async clearAllTabs(): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    await this.db.clear('tabs');
  }

  async saveAppState(key: string, value: any): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    await this.db.put('appState', { key, value });
  }

  async loadAppState(key: string): Promise<any> {
    if (!this.db) throw new Error('Database not initialized');
    const result = await this.db.get('appState', key);
    return result?.value;
  }
}
